import { createContext, useContext, useState, useEffect } from "react";
import { useGlobalSearchParams } from "expo-router";

const AcquisitionContext = createContext();

/**
 * Hook to access acquisition tracking context
 * @returns {{ acquisitionRef: string | null, fbclid: string | null, buildUrlWithParams: (path: string) => string, clearAcquisitionData: () => void }}
 */
export const useAcquisition = () => {
  const context = useContext(AcquisitionContext);
  if (!context) {
    throw new Error("useAcquisition must be used within AcquisitionProvider");
  }
  return context;
};

/**
 * AcquisitionProvider
 * Captures and maintains the 'ref' and 'fbclid' URL parameters throughout the user session
 *
 * This enables tracking of marketing campaign sources (e.g., Instagram ads, Facebook ads)
 * without using localStorage or cookies.
 *
 * Convention for ref values:
 * - Instagram ads: ref=ig_ad_launch_video1, ref=ig_ad_es_coaches_carousel3
 * - Instagram profile: ref=ig_profile_bio_main, ref=ig_profile_linktree_trial
 *
 * The fbclid parameter is automatically added by Facebook to track ad clicks.
 *
 * Both parameters are:
 * - Captured once when user first enters the app
 * - Maintained in memory during the session
 * - Propagated through all navigation within the funnel
 * - Saved to Firestore when coach completes onboarding
 * - Lost if user closes/reloads without the parameters
 */
export const AcquisitionProvider = ({ children }) => {
  const [acquisitionRef, setAcquisitionRef] = useState(null);
  const [fbclid, setFbclid] = useState(null);
  const params = useGlobalSearchParams();

  useEffect(() => {
    // Capture the 'ref' parameter from URL if present
    // Note: Other URL parameters (like 'lang') are handled separately by their own hooks
    // and will not interfere with acquisition tracking
    const refParam = params.ref;

    if (refParam && !acquisitionRef) {
      console.log("[AcquisitionContext] Captured ref parameter:", refParam);
      setAcquisitionRef(refParam);
    }
  }, [params.ref, acquisitionRef]);

  useEffect(() => {
    // Capture the 'fbclid' parameter from URL if present
    // This is Facebook's click identifier automatically added to URLs from Facebook ads
    const fbclidParam = params.fbclid;

    if (fbclidParam && !fbclid) {
      console.log(
        "[AcquisitionContext] Captured fbclid parameter:",
        fbclidParam
      );
      setFbclid(fbclidParam);
    }
  }, [params.fbclid, fbclid]);

  /**
   * Helper function to build URLs with acquisition parameters
   * @param {string} path - The path to navigate to (e.g., '/signup', '/login')
   * @returns {string} The path with ref and/or fbclid parameters appended if they exist
   */
  const buildUrlWithParams = (path) => {
    if (!acquisitionRef && !fbclid) return path;

    const params = [];
    if (acquisitionRef) params.push(`ref=${acquisitionRef}`);
    if (fbclid) params.push(`fbclid=${fbclid}`);

    const separator = path.includes("?") ? "&" : "?";
    return `${path}${separator}${params.join("&")}`;
  };

  /**
   * Clear the acquisition data (e.g., on logout)
   */
  const clearAcquisitionData = () => {
    console.log("[AcquisitionContext] Clearing acquisition data");
    setAcquisitionRef(null);
    setFbclid(null);
  };

  const value = {
    acquisitionRef,
    fbclid,
    buildUrlWithParams,
    clearAcquisitionData,
  };

  return (
    <AcquisitionContext.Provider value={value}>
      {children}
    </AcquisitionContext.Provider>
  );
};
